/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import * as textConstants from '../../constants/StudioTextConstants';
import utils from '../../../../common/utils';

export default class ShareGraphService extends BaseService {
  constructor() {
    super(constants.SHARE_GRAPH, 'shareGraphStatus');
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { graphId, emails, accessType } = payload;

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').post(
        `/graphs/${graphId}/share`,
        { emails, accessType },
        { headers: enhancedHeaders }
      );
    }
    return this.getClient('aiadmin').post(`/graphs/${graphId}/share`, { emails, accessType }, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    const shareGraphModel = Framework.getStoreRegistry().getStore('shareGraphModel');

    if (data && data.success) {
      shareGraphModel.setResponse({
        sharedWith: data.sharedWith || [],
        notFound: data.notFound || []
      });

      // Show success notification only if at least one user was shared with
      if (data.sharedWith && data.sharedWith.length > 0) {
        const notification = {
          open: true,
          type: data.notFound?.length > 0 ? 'warning' : 'success',
          message: `${textConstants.SHARE_GRAPH_SUCCESS_SHARED_WITH} ${data.sharedWith.length} user(s).`
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
      } else if (data.notFound && data.notFound.length > 0) {
        const notification = {
          open: true,
          type: 'warning',
          message: textConstants.SHARE_GRAPH_ERROR_NONE_FOUND
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
      }
    } else {
      shareGraphModel.setError(data?.message || textConstants.SHARE_GRAPH_ERROR_GENERIC);
    }
  }

  handleError(error) {
    const shareGraphModel = Framework.getStoreRegistry().getStore('shareGraphModel');
    const { response } = error;

    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
      shareGraphModel.setError(data?.message || textConstants.SHARE_GRAPH_ERROR_GENERIC);
      const notification = {
        open: true,
        message: `${textConstants.SHARE_GRAPH_ERROR_PREFIX}: ${
          data?.message || textConstants.SHARE_GRAPH_ERROR_GENERIC
        }`,
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
    } else if (error.message === 'timeout of 60000ms exceeded') {
      shareGraphModel.setError('Request timeout. Please try again.');
      const notification = {
        open: true,
        message: 'Request timeout. Please try again.',
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
    } else {
      shareGraphModel.setError(error.message || textConstants.SHARE_GRAPH_ERROR_GENERIC);
    }
  }
}
